import React, { FunctionComponent } from 'react'
import { Timeline, Text } from '@afterpaytouch/core'
import type { CheckoutPaymentSchedule } from '@afterpaytouch/portal-api'
import type { Money as MoneyValue } from '@afterpay/types'
import { TradeInDisclaimer } from './TradeInDisclaimer'
import { getSchedulePreview } from '../utils/getSchedulePreview'
import { useTranslation, Trans } from '../../../../../utils/convergence'
import { Money } from '../../../../../component'
import styles from './styles.module.css'

interface FinancingDetailsProps {
  id: string
  index: number // test purpose
  name: string
  schedules: CheckoutPaymentSchedule[]
  numberOfInstallments?: number
  hasDownpayment: boolean
  downPaymentAmount?: MoneyValue
  monthlyAmount?: MoneyValue
  showTradeInDisclaimer: boolean
}

export const FinancingDetails: FunctionComponent<FinancingDetailsProps> = ({
  id,
  index,
  schedules,
  numberOfInstallments,
  hasDownpayment,
  downPaymentAmount,
  monthlyAmount,
  showTradeInDisclaimer,
}) => {
  const { t } = useTranslation(['summary'])
  const preview = getSchedulePreview(schedules)
  const firstPreview = preview[0]
  const lastPreview = preview.length > 1 ? preview[preview.length - 1] : undefined

  const downpaymentTestNameSpace = `financed-item-${index}-downpayment`
  const firstPaymentTestNameSpace = `financed-item-${index}-first-payment`
  const monthlyPaymentTestNameSpace = `financed-item-${index}-monthly-payment`
  const lastPaymentTestNameSpace = `financed-item-${index}-last-payment`

  return (
    <>
      <Timeline kind='Vertical'>
        {hasDownpayment && (
          <Timeline.Item
            highlight
            titleText={
              <span className={styles.timelineItemContainer}>
                <Text size='S' bold renderAs='span' testNameSpace={downpaymentTestNameSpace}>
                  {t('appleRetail.downpaymentToday')}
                </Text>
              </span>
            }
            bodyText={{
              text: <Money size='M' bold value={downPaymentAmount} testNameSpace={downpaymentTestNameSpace} />,
            }}
          />
        )}
        {typeof firstPreview !== 'undefined' && (
          <Timeline.Item
            highlight={!hasDownpayment}
            titleText={
              <span className={styles.timelineItemContainer}>
                <Text size='S' bold renderAs='span' testNameSpace={firstPaymentTestNameSpace}>
                  {t('appleRetail.paymentXofY', {
                    paymentNumber: 1,
                    totalPayments: numberOfInstallments,
                  })}
                </Text>
              </span>
            }
            bodyText={{
              text: <Money size='M' bold value={firstPreview.schedule.amount} testNameSpace={firstPaymentTestNameSpace} />,
            }}
          />
        )}
        <Timeline.Item
          nodeKind='Ellipsis'
          titleText={
            <span className={styles.timelineItemContainer}>
              <Text size='S' bold renderAs='span' testNameSpace={monthlyPaymentTestNameSpace}>
                <Trans
                  i18nKey='summary:appleRetail.everyMonth'
                  components={{
                    money: <Money bold value={monthlyAmount} size='S' testNameSpace={monthlyPaymentTestNameSpace} />,
                  }}
                />
              </Text>
            </span>
          }
          bodyText={{
            text: '',
          }}
        />
        {typeof lastPreview !== 'undefined' && lastPreview !== firstPreview && (
          <Timeline.Item
            hideLine
            titleText={
              <Text size='S' bold renderAs='span' testNameSpace={lastPaymentTestNameSpace}>
                {t('appleRetail.paymentXofY', {
                  paymentNumber: numberOfInstallments,
                  totalPayments: numberOfInstallments,
                })}
              </Text>
            }
            bodyText={{
              text: <Money size='M' bold value={lastPreview?.schedule.amount} testNameSpace={lastPaymentTestNameSpace} />,
            }}
          />
        )}
      </Timeline>
      <TradeInDisclaimer show={showTradeInDisclaimer} />
    </>
  )
}
